import type { TeamMember } from '@nexo/types';
import { Avatar, Button, avatarColor, cn } from '@nexo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Crown, MessageSquare, Pencil, Shield } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
  type MouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { cache } from '../../lib/cache';
import { formatShortDate } from '../../lib/format';
import { useMe } from '../auth/AuthProvider';
import { UserBadges, statusLabel } from '../status/UserBadges';
import { RoleChip } from './roles';

const WIDTH = 320;
const EDGE = 8;

/** Whose card is open and what was clicked to open it (one card for the whole app). */
let current: { userId: string; anchor: DOMRect } | null = null;
const listeners = new Set<() => void>();
const setCurrent = (next: typeof current) => {
  current = next;
  listeners.forEach((listener) => listener());
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useOpenUserCardId = () => useSyncExternalStore(subscribe, () => current?.userId);

/**
 * Opens someone's card beside the element that was clicked; clicking it again closes it. Mark the
 * clickable element with `data-user-card={userId}` so that click doesn't count as «outside».
 */
export function openUserCard(userId: string, event: MouseEvent<HTMLElement>) {
  const anchor = event.currentTarget.getBoundingClientRect();
  setCurrent(current?.userId === userId ? null : { userId, anchor });
}

/** Renders the open card, if any. Mounted once in the team shell. */
export function UserCardHost() {
  const open = useSyncExternalStore(subscribe, () => current);
  const { team, memberById } = useTeam();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const close = useCallback(() => setCurrent(null), []);
  const openDm = useMutation({
    mutationFn: (userId: string) => api.channels.openDm(team.id, userId),
    onSuccess: (channel) => {
      cache.upsertChannel(queryClient, channel);
      void navigate(`/app/t/${team.id}/c/${channel.id}`);
    },
  });

  // Close it when switching teams.
  useEffect(() => close, [team.id, close]);

  const member = open && memberById(open.userId);
  if (!open || !member) return null;
  return (
    <UserCard
      member={member}
      anchor={open.anchor}
      onClose={close}
      onMessage={() => openDm.mutate(member.id)}
    />
  );
}

/**
 * Someone's profile card, like Discord's: opens beside the person you clicked (to the left of the
 * members panel) with their name in full, status, roles here and what Nexo knows about them
 * globally (since when they use Nexo, teams in common).
 */
function UserCard({
  member,
  anchor,
  onClose,
  onMessage,
}: {
  member: TeamMember;
  anchor: DOMRect;
  onClose: () => void;
  onMessage: () => void;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const { team, online, roleById } = useTeam();
  const card = useRef<HTMLDivElement>(null);
  const isMe = member.id === me.id;
  const profile = useQuery({
    queryKey: ['user-profile', member.id],
    queryFn: () => api.users.profile(member.id),
    staleTime: 60_000,
  });

  // Beside what was clicked (to its left on the right half of the window, like the members
  // panel; to its right otherwise, like names in the chat), kept inside the window.
  useLayoutEffect(() => {
    const node = card.current;
    if (!node) return;
    const height = node.offsetHeight;
    const onRight = anchor.left + anchor.width / 2 > window.innerWidth / 2;
    const left = onRight
      ? anchor.left - WIDTH - EDGE
      : Math.min(anchor.right + EDGE, window.innerWidth - WIDTH - EDGE);
    const top = Math.max(EDGE, Math.min(anchor.top, window.innerHeight - height - EDGE));
    node.style.left = `${Math.max(EDGE, left)}px`;
    node.style.top = `${top}px`;
  });

  useEffect(() => {
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Element;
      if (card.current?.contains(target)) return;
      // Clicking the same person again toggles it from their own click handler.
      if (target.closest(`[data-user-card="${member.id}"]`)) return;
      onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, member.id]);

  const status = statusLabel(member);
  const mutual = profile.data?.mutualTeams.filter((other) => other.id !== team.id) ?? [];

  return createPortal(
    <div
      ref={card}
      role="dialog"
      aria-label={member.displayName}
      style={{ width: WIDTH, left: -9999, top: 0 }}
      className="fixed z-50 animate-fade-in overflow-hidden rounded-2xl border border-border bg-surface shadow-float"
    >
      <div className={cn('h-16', avatarColor(member.id))} />
      <div className="px-4 pb-4">
        <div className="-mt-9 flex items-end justify-between">
          <span className="rounded-full ring-4 ring-surface">
            <Avatar
              id={member.id}
              name={member.displayName}
              src={member.avatarUrl}
              size="lg"
              online={online ? online.has(member.id) : undefined}
            />
          </span>
          {member.role === 'owner' && (
            <Crown className="mb-1 size-5 text-amber-500" aria-label={t('Propietario')}>
              <title>{t('Propietario')}</title>
            </Crown>
          )}
          {member.role === 'admin' && (
            <Shield className="mb-1 size-5 text-accent" aria-label={t('Admin')}>
              <title>{t('Admin')}</title>
            </Shield>
          )}
        </div>

        <div className="mt-2 rounded-xl bg-surface-2 p-3">
          <p className="text-lg leading-tight font-semibold break-words">
            {member.displayName}
            <UserBadges user={member} className="ml-1.5" />
          </p>
          {member.title && <p className="mt-0.5 text-sm text-muted">{member.title}</p>}
          {status && <p className="mt-2 text-sm break-words">{status}</p>}

          <div className="mt-3 space-y-3 border-t border-border pt-3 text-sm">
            {(member.role !== 'member' || member.customRoleIds.length > 0) && (
              <section>
                <h3 className="mb-1 text-[11px] font-semibold tracking-wider text-subtle uppercase">
                  {t('Roles')}
                </h3>
                <div className="flex flex-wrap gap-1">
                  {member.role !== 'member' && (
                    <span className="inline-flex items-center rounded-full bg-surface px-2 py-0.5 text-[11px] font-medium">
                      {member.role === 'owner' ? t('Propietario') : t('Admin')}
                    </span>
                  )}
                  {member.customRoleIds.map((id) => {
                    const role = roleById(id);
                    return role && <RoleChip key={id} role={role} />;
                  })}
                </div>
              </section>
            )}
            <section>
              <h3 className="mb-1 text-[11px] font-semibold tracking-wider text-subtle uppercase">
                {t('Miembro desde')}
              </h3>
              <dl className="space-y-0.5 text-muted">
                {[
                  { id: 'nexo', label: 'Nexo', date: profile.data?.createdAt },
                  { id: 'team', label: team.name, date: member.joinedAt },
                ].map((row) => (
                  <div key={row.id} className="flex items-baseline gap-3">
                    <dt className="min-w-0 flex-1 truncate" title={row.label}>
                      {row.label}
                    </dt>
                    <dd className="shrink-0 text-text">
                      {row.date ? formatShortDate(row.date) : '…'}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
            {!isMe && mutual.length > 0 && (
              <section>
                <h3 className="mb-1 text-[11px] font-semibold tracking-wider text-subtle uppercase">
                  {t('Equipos en común')} · {mutual.length}
                </h3>
                <ul className="flex flex-wrap gap-1">
                  {mutual.map((other) => (
                    <li key={other.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onClose();
                          void navigate(`/app/t/${other.id}`);
                        }}
                        className="max-w-[12rem] truncate rounded-full bg-surface px-2 py-0.5 text-[11px] font-medium hover:bg-surface-3"
                      >
                        {other.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>

        {isMe ? (
          <Button
            variant="secondary"
            size="sm"
            icon={<Pencil className="size-4" />}
            className="mt-3 w-full"
            onClick={() => {
              onClose();
              void navigate('/app/profile');
            }}
          >
            {t('Editar perfil')}
          </Button>
        ) : (
          <Button
            size="sm"
            icon={<MessageSquare className="size-4" />}
            className="mt-3 w-full"
            onClick={() => {
              onClose();
              onMessage();
            }}
          >
            {t('Enviar mensaje')}
          </Button>
        )}
      </div>
    </div>,
    document.body,
  );
}
